import _ from 'underscore';

import { ContentMapper } from '../../../ts/mappers/ContentMapper';
import { MapperHelper } from '../../../ts/mappers/MapperHelper';
import { UserMapper } from '../../../ts/mappers/UserMapper';
import { ILocationInfo } from '../../../ts/models/ILocationInfo';
import { IUserInfo } from '../../../ts/models/IUserInfo';
import { INamedItemInfo } from '../../../ts/models/resourceInfoParts';
import { IUsageInfo } from '../../../ts/ServerApi/types/IUsageInfo';
import { DataRoleFieldSynonymWebClient } from '../../apis/UpdateDatarole';
import { IDataRole, IDataRoleDetail } from '../../serverApi/types/IDataRole';
import { ConceptName } from '../../types/ConceptName';
import { compareValueConcepts } from '../../utils/searchUtils';
import { IDataRoleDetailInfo, IDataRoleInfo } from '../IDataRoleInfo';

export module DataRoleMapper {

  const M = MapperHelper;

  export function map(
    serverApiDataRoles: IDataRole[],
    users: _.Dictionary<IUserInfo>,
    favoriteDataRoles: _.Dictionary<any> = {},
    projects: _.Dictionary<INamedItemInfo>,
    locations: ILocationInfo[] = []
  ): IDataRoleInfo[] {
    const dataroleToProject = ContentMapper.getContentToProjectMap(ContentMapper.ContentTypeToTargetMap.dataRole, projects);
    const dataroleToLocation = ContentMapper.getContentToLocationMap(ContentMapper.ContentTypeToTargetMap.dataRole, locations);
    return _.map(serverApiDataRoles, (p: IDataRole) => {
      const drInfo: IDataRoleInfo = {
        id:                           p.id,
        luid:                         p.luid,
        name:                         p.name,
        description:                  p.description,
        owner:                        UserMapper.mapOne(users[p.ownerId]),
        updatedAt:                    M.toDate(p.updatedAt),
        contentType:                  'dataRole',
        project:                      dataroleToProject[p.id],
        location:                     dataroleToLocation[p.id],
        favorite:                     !!favoriteDataRoles[p.id],
        usageInfo:                    {
                                        favoritesTotal: 0,
                                        hitsTotal: 0,
                                        hitsLastOneMonthTotal: 0,
                                        hitsLastThreeMonthsTotal: 0,
                                        hitsLastTwelveMonthsTotal: 0,
                                      },
        usageInfoMaxValues:           {
                                        favoritesTotal: 0,
                                        hitsTotal: 0,
                                        hitsLastOneMonthTotal: 0,
                                        hitsLastThreeMonthsTotal: 0,
                                        hitsLastTwelveMonthsTotal: 0,
                                      },
      };
      return drInfo;
    });
  }

  export function mapDetail(p: IDataRoleDetail): IDataRoleDetailInfo {
    const fieldConceptKey = extractFieldConceptKeyFromFieldConceptUri(p.fieldConceptUri);

    const valueConceptArray = new Array(p.valueCount);
    p.values
      .filter(x => !!x)
      .map(value => ({
        uri: '',
        names: [],
        value: {
          string_value: value.stringValue,
          number_value: value.numberValue,
          bool_value: value.boolValue,
          time_value: value.timeValue,
        }
      }))
      .sort(compareValueConcepts)
      .forEach((value, index) => valueConceptArray[index] = value);

    return {
      id:                           p.id,
      luid:                         p.luid,
      owner:                        UserMapper.mapOne(p.owner),
      updatedAt:                    M.toDate(p.updatedAt),
      contentType:                  'dataRole',
      favorite:                     p.favorite,
      name: p.name,
      description: p.description,
      fieldConceptUri: p.fieldConceptUri,

      dataRole: {
        name: p.name,
        description: p.description,
        field_concept: {
          names: p.fieldConceptNames
          .map((conceptName: DataRoleFieldSynonymWebClient): ConceptName => {
            const {isPrimary, ...conceptShared} = conceptName;
            return {...conceptShared, is_primary: isPrimary};
          }),
          data_types: p.dataType,
          data_role_id: p.id,
          key: fieldConceptKey
        },
        field_concept_key: fieldConceptKey,
        value_concepts: valueConceptArray,
        value_count: p.valueCount,
      },
    };
  }

  export function mapFromCRASS(content: any): IDataRoleInfo {
    return {
      updatedAt: M.toDate(content.modifiedTime) || M.toDateFromTimeOffsetObject(content.modifiedTime),
      description: content.description,
      id: content.id + '',
      luid: content.luid,
      name: content.title,
      favorite: false,
      contentType: 'dataRole',
      owner: UserMapper.mapOneOwnerFromCRASS(content),
      usageInfo: <IUsageInfo> {}, // does not exist in CRASS yet
      usageInfoMaxValues: <IUsageInfo> {}, // does not exist in CRASS yet
      project: {
        id: content.projectId ?? content.parentProjectId,
        name: content.containerName
      },
    };
  }
}

// Backend sends over full Uri, but URL construction for update requests only needs the last few characters
const extractFieldConceptKeyFromFieldConceptUri = (uri: string): string => {
  const matches: RegExpExecArray = /ontologies\/.+\/fieldConcepts\/(.+)/.exec(uri);
  return matches && matches[1] || '';
};
